import { schoolsSchema } from "@workspace/public-seo";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, School } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";

const DISTRICTS = [
  "全部","中西區","灣仔區","東區","南區","油尖旺區","深水埗區","九龍城區",
  "黃大仙區","觀塘區","葵青區","荃灣區","屯門區","元朗區","北區",
  "大埔區","沙田區","西貢區","離島區",
];

interface SchoolItem {
  id: number;
  name: string;
  district: string;
  schoolType: string;
}

export default function SchoolsPage() {
  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("全部");
  const [type, setType] = useState("全部");

  const { data: schools = [], isLoading } = useQuery<SchoolItem[]>({
    queryKey: ["schools-public"],
    queryFn: async () => {
      const res = await fetch("/api/schools");
      if (!res.ok) throw new Error("載入失敗");
      return res.json();
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schools.filter(s => {
      if (district !== "全部" && s.district !== district) return false;
      if (type === "中學" && s.schoolType !== "secondary") return false;
      if (type === "小學" && s.schoolType !== "primary") return false;
      if (q && !s.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [schools, search, district, type]);

  const secondaryCount = schools.filter(s => s.schoolType === "secondary").length;
  const primaryCount = schools.filter(s => s.schoolType === "primary").length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title="學校名單"
        description="瀏覽 Skoltend 收錄的全港中小學名單，涵蓋 18 區逾千所學校。掌握各區學校分佈，精準追蹤目標學校的最新招標及書面報價資訊。"
        canonical="/schools"
        structuredData={schoolsSchema(filtered)}
      />
      <div className="flex-1">
        {/* Header */}
        <div className="bg-primary text-primary-foreground py-10">
          <div className="container mx-auto px-4 md:px-8">
            <div className="flex items-center gap-3 mb-2">
              <School className="h-7 w-7 text-primary-foreground/80" />
              <h1 className="text-2xl font-bold">覆蓋學校</h1>
            </div>
            <p className="text-primary-foreground/70 text-sm mb-4">
              Skoltend 資料庫所收錄的全港中小學名單
            </p>
            <div className="flex gap-4 text-sm">
              <span className="text-primary-foreground/80">全部 <strong>{schools.length}</strong> 間</span>
              <span className="text-primary-foreground/80">中學 <strong>{secondaryCount}</strong> 間</span>
              <span className="text-primary-foreground/80">小學 <strong>{primaryCount}</strong> 間</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-8 py-8">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="搜尋學校名稱..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {["全部","中學","小學"].map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                    type === t
                      ? "bg-primary text-primary-foreground border-primary font-medium"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* District filter */}
          <div className="flex flex-wrap gap-1.5 mb-6">
            {DISTRICTS.map(d => (
              <button
                key={d}
                onClick={() => setDistrict(d)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                  district === d
                    ? "bg-primary text-primary-foreground border-primary font-medium"
                    : "border-border text-muted-foreground hover:border-primary/40"
                }`}
              >
                {d}
              </button>
            ))}
          </div>

          {/* Count */}
          <p className="text-sm text-muted-foreground mb-4">
            顯示 <span className="font-medium text-foreground">{filtered.length}</span> 間學校
          </p>

          {/* List */}
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 20 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm py-12 text-center">找不到相關學校</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {filtered.map(s => (
                <div key={s.id} className="flex items-start justify-between gap-2 rounded-lg border bg-card px-3 py-2.5 hover:border-primary/30 hover:bg-primary/[0.02] transition-colors">
                  <span className="text-sm font-medium leading-snug">{s.name}</span>
                  <div className="flex flex-col items-end gap-1 shrink-0 mt-0.5">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
                      {s.schoolType === "primary" ? "小學" : s.schoolType === "secondary" ? "中學" : "其他"}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{s.district}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
